/**
 * @fileoverview Helpers for the letterhead sample-page preview.
 *
 * Kept outside the component file so the preview component only exports
 * components (react-refresh) and so margin editors can convert between
 * the ten diagram values and a resolved `DocumentPadding` without pulling
 * in the preview itself.
 */

import type { DocumentPadding } from "./resolve-letterhead";
import type { DiagramValues } from "@/components/branding/MarginsRulerDiagram";

/**
 * Sample letter content shown in the body of the preview. Inline styles
 * only — the preview page is style-isolated from the app.
 */
export const SAMPLE_LETTER_BODY_HTML = [
  `<p style="margin:0 0 14px;">26 August 2026</p>`,
  `<p style="margin:0 0 14px;"><strong>Private &amp; Confidential</strong><br/>By email: j.sample@example.com.au</p>`,
  `<p style="margin:0 0 14px;">Ms J Sample<br/>Director<br/>Sample Trading Pty Ltd<br/>1 Example Street<br/>Sydney NSW 2000</p>`,
  `<p style="margin:0 0 14px;">Dear Ms Sample,</p>`,
  `<p style="margin:0 0 14px;"><strong>Sample Trading Pty Ltd (In Liquidation) ACN 123 456 789<br/>Notice of appointment of liquidator</strong></p>`,
  `<p style="margin:0 0 14px;">I refer to the resolution of the members of the Company passed on 22 August 2026 appointing me as liquidator of the Company pursuant to section 491 of the <em>Corporations Act 2001</em> (Cth). This letter sets out the information I require from you as a director and the steps I will take over the coming weeks.</p>`,
  `<p style="margin:0 0 14px;">This placeholder text is here only to show how a letter sits on the letterhead. The spacing around the header, body and footer follows the margin settings, so what you see in this preview matches the exported document.</p>`,
  `<p style="margin:0 0 8px;">Within seven days of the date of this letter, please provide:</p>`,
  `<ol style="margin:0 0 14px 22px;padding:0;">` +
    `<li style="margin:0 0 4px;">a completed Report on Company Activities and Property (ROCAP);</li>` +
    `<li style="margin:0 0 4px;">the Company's books and records, including bank statements, the general ledger and payroll records;</li>` +
    `<li style="margin:0 0 4px;">details of any assets held by third parties or subject to security interests; and</li>` +
    `<li style="margin:0;">a list of employees and any outstanding entitlements.</li>` +
    `</ol>`,
  `<p style="margin:0 0 14px;">Please note that a failure to assist the liquidator is an offence under the Act. If any of the requested information is unavailable, let me know as soon as possible so we can agree a practical way forward.</p>`,
  `<p style="margin:0 0 14px;">Should you have any questions, please contact Sam Example of this office on (02) 9000 0000 or at sam.example@firm.com.au.</p>`,
  `<p style="margin:0 0 28px;">Yours faithfully,</p>`,
  `<p style="margin:0;"><strong>A Practitioner</strong><br/>Liquidator<br/>Sample Trading Pty Ltd (In Liquidation)</p>`,
].join("");

/** Resolved padding → the ten editable diagram values. */
export function paddingToDiagramValues(p: DocumentPadding): DiagramValues {
  return {
    headerTopPadding: p.headerTop,
    headerLeftPadding: p.headerLeft,
    headerRightPadding: p.headerRight,
    bodyTopPadding: p.bodyTop,
    bodyLeftPadding: p.bodyLeft,
    bodyRightPadding: p.bodyRight,
    bodyBottomPadding: p.bodyBottom,
    footerBottomPadding: p.footerBottom,
    footerLeftPadding: p.footerLeft,
    footerRightPadding: p.footerRight,
  };
}

/** Ten diagram values → a resolved padding (symmetric side = min pair). */
export function diagramValuesToPadding(v: DiagramValues): DocumentPadding {
  return {
    headerTop: v.headerTopPadding,
    headerLeft: v.headerLeftPadding,
    headerRight: v.headerRightPadding,
    headerSide: Math.min(v.headerLeftPadding, v.headerRightPadding),
    bodyTop: v.bodyTopPadding,
    bodyLeft: v.bodyLeftPadding,
    bodyRight: v.bodyRightPadding,
    bodyBottom: v.bodyBottomPadding,
    bodySide: Math.min(v.bodyLeftPadding, v.bodyRightPadding),
    footerBottom: v.footerBottomPadding,
    footerLeft: v.footerLeftPadding,
    footerRight: v.footerRightPadding,
    footerSide: Math.min(v.footerLeftPadding, v.footerRightPadding),
  };
}
